'use client';

/**
 * OperatorEventToasts — live control-plane notifications.
 *
 * Subscribes to the operator WS stream (/ws/streams/user) and pops a toast for
 * every `operator_event` the backend broadcasts: deployment start/stop, arm/
 * disarm, order failures, risk halts, instrument sync — the same events that
 * fire Telegram. Auto-dismiss; newest on top; bounded stack.
 */
import * as React from 'react';
import { AlertTriangle, Bell, X } from 'lucide-react';

import { subscribeQueryFailures } from '@/lib/query/failure-bus';
import { wsManager } from '@/lib/ws/manager';
import { cn } from '@/lib/util/cn';
import type { WsAllEvents } from '@/types/events';

type Toast = { id: number; kind: string; subject: string; detail: string };

const DISMISS_MS = 7000;
const MAX_VISIBLE = 5;

function isCritical(kind: string): boolean {
  const k = kind.toLowerCase();
  return ['fail', 'halt', 'breach', 'breaker', 'risk', 'error', 'reject'].some((w) => k.includes(w));
}

export function OperatorEventToasts(): React.ReactElement {
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  const seq = React.useRef(0);

  const dismiss = React.useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  React.useEffect(() => {
    const off = wsManager.subscribe((e: WsAllEvents) => {
      if ((e as { type?: string }).type !== 'operator_event') {return;}
      const ev = e as { kind: string; subject: string; detail: string };
      const id = ++seq.current;
      setToasts((t) => [{ id, kind: ev.kind, subject: ev.subject, detail: ev.detail }, ...t].slice(0, MAX_VISIBLE));
      setTimeout(() => dismiss(id), DISMISS_MS);
    });
    return off;
  }, [dismiss]);

  // Data-fetch failures. Without this a failed query renders as an empty list
  // — "I couldn't ask" looks exactly like "the answer is zero". Published from
  // the global QueryCache.onError so EVERY page is covered, including pages
  // that never handle `isError` themselves. See lib/query/failure-bus.
  React.useEffect(() => {
    return subscribeQueryFailures((f) => {
      const id = ++seq.current;
      setToasts((t) =>
        [{ id, kind: 'data_fetch_failed', subject: f.subject, detail: f.detail }, ...t].slice(
          0,
          MAX_VISIBLE,
        ),
      );
      setTimeout(() => dismiss(id), DISMISS_MS);
    });
  }, [dismiss]);

  if (toasts.length === 0) {return <></>;}

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-80 flex-col gap-2">
      {toasts.map((t) => {
        const crit = isCritical(t.kind);
        const Icon = crit ? AlertTriangle : Bell;
        return (
          <div
            key={t.id}
            className={cn(
              'pointer-events-auto flex items-start gap-2 rounded border px-3 py-2 shadow-lg backdrop-blur',
              'animate-in slide-in-from-right-2 fade-in duration-200',
              crit
                ? 'border-crit/50 bg-crit-soft text-crit'
                : 'border-border bg-bg-elev/95 text-fg',
            )}
          >
            <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <div className="min-w-0 flex-1">
              <div className="truncate text-2xs font-semibold">
                {t.subject}
              </div>
              {t.detail ? (
                <div className="mt-0.5 truncate text-2xs text-fg-muted">{t.detail}</div>
              ) : null}
            </div>
            <button
              onClick={() => dismiss(t.id)}
              className="shrink-0 text-fg-subtle hover:text-fg"
              aria-label="Dismiss"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
